import React from 'react';
import {
  AlertTriangle,
  AlertCircle,
  Activity,
  XCircle,
  FileWarning,
  TrendingUp,
  Clock,
  GitCompare,
  Terminal,
  Shield,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import * as Diff from 'diff';

// Format timestamp (unix seconds) to full datetime in UTC.
export function formatDateTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
    timeZoneName: 'short',
  });
}

// Format timestamp (unix seconds) to time only in UTC.
export function formatTime(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'UTC',
  });
}

// Get initials from an email address for avatar display.
export function getInitials(email: string): string {
  if (!email) return '?';
  const parts = email.split('@')[0].split(/[._-]/).filter((p) => p.length > 0);
  if (parts.length >= 2) {
    return (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
  }
  return email.split('@')[0].charAt(0).toUpperCase();
}

// Severity icon shared by both K8s and cloud incident views.
export function getSeverityIcon(severity: string, className?: string) {
  const classes = className || 'h-5 w-5';
  switch (severity) {
    case 'critical':
      return <AlertTriangle className={cn(classes, 'text-red-500')} />;
    case 'high':
      return <AlertCircle className={cn(classes, 'text-orange-500')} />;
    case 'medium':
      return <AlertCircle className={cn(classes, 'text-yellow-500')} />;
    case 'low':
      return <AlertCircle className={cn(classes, 'text-blue-500')} />;
    default:
      return <Activity className={cn(classes, 'text-gray-500')} />;
  }
}

export function getSeverityBadgeVariant(
  severity: string,
): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (severity) {
    case 'critical':
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    default:
      return 'secondary';
  }
}

// Timeline icon for the K8s "Chain of Events" (investigation_summary.timeline_of_events[].icon).
export function getTimelineIcon(iconType: string) {
  // Monochrome: black in light mode, white in dark mode (theme foreground).
  const cls = 'h-4 w-4 text-foreground';
  switch (iconType) {
    case 'alert':
      return <AlertTriangle className={cls} />;
    case 'log':
      return <FileWarning className={cls} />;
    case 'error':
      return <XCircle className={cls} />;
    case 'status':
      return <Activity className={cls} />;
    case 'metric':
      return <TrendingUp className={cls} />;
    case 'deployment':
      return <GitCompare className={cls} />;
    default:
      return <Clock className={cls} />;
  }
}

// Timeline icon for the K8s rca_result.timeline[] (by event_type).
export function getEventTypeIcon(eventType: string, severity: string) {
  switch (eventType) {
    case 'alert':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'pagerduty_alert':
      return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
    case 'log_entry':
      return severity === 'ERROR' || severity === 'FATAL' ? (
        <XCircle className="h-4 w-4 text-red-500" />
      ) : (
        <FileWarning className="h-4 w-4 text-orange-500" />
      );
    case 'status_change':
      return <Activity className="h-4 w-4 text-blue-500" />;
    case 'deployment':
      return <TrendingUp className="h-4 w-4 text-purple-500" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
}

// Timeline icon for the cloud rca_result.timeline[] (by event_type).
export function getCloudTimelineEventIcon(eventType: string, _severity: string) {
  switch (eventType) {
    case 'api_call':
      return <Terminal className="h-4 w-4 text-muted-foreground" />;
    case 'config_change':
      return <TrendingUp className="h-4 w-4 text-muted-foreground" />;
    case 'metric_anomaly':
      return <Activity className="h-4 w-4 text-muted-foreground" />;
    case 'alert':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'compliance_violation':
      return <Shield className="h-4 w-4 text-red-500" />;
    case 'pagerduty_alert':
      return <AlertTriangle className="h-4 w-4 text-muted-foreground" />;
    default:
      return <Clock className="h-4 w-4 text-gray-500" />;
  }
}

export function getRiskLevelBadgeVariant(
  riskLevel: string,
): 'default' | 'destructive' | 'outline' | 'secondary' {
  switch (riskLevel?.toLowerCase()) {
    case 'high':
      return 'destructive';
    case 'medium':
      return 'default';
    case 'low':
      return 'secondary';
    default:
      return 'outline';
  }
}

// Get K8s incident type label.
export function getIncidentTypeLabel(type: string): string {
  switch (type) {
    case 'OOMKilled':
      return 'Out of Memory';
    case 'CrashLoopBackOff':
      return 'Crash Loop BackOff';
    case 'ImagePullError':
      return 'Image Pull Error';
    case 'RolloutFailure':
      return 'Rollout Failure';
    case 'NodeNotReady':
      return 'Node Not Ready';
    case 'MissingPVC':
      return 'Missing PersistentVolumeClaim';
    case 'InsufficientResources':
      return 'Insufficient Cluster Resources';
    case 'SchedulingFailure':
      return 'Pod Scheduling Failure';
    case 'TaintTolerationMismatch':
      return 'Taint/Toleration Mismatch';
    case 'NodeSelectorMismatch':
      return 'Node Selector Mismatch';
    case 'PodEvicted':
      return 'Pod Evicted';
    case 'LivenessProbeFailure':
      return 'Liveness Probe Failure';
    case 'ReadinessProbeFailure':
      return 'Readiness Probe Failure';
    case 'HealthCheckFailure':
      return 'Health Check Failure';
    case 'VolumeError':
      return 'Volume Error';
    case 'VolumeResizeError':
      return 'Volume Resize Error';
    case 'NetworkError':
      return 'Network Error';
    case 'RBACError':
      return 'RBAC Error';
    case 'ResourceExhaustion':
      return 'Resource Exhaustion';
    case 'ContainerRuntimeError':
      return 'Container Runtime Error';
    case 'LifecycleHookError':
      return 'Lifecycle Hook Error';
    case 'ConfigError':
      return 'Configuration Error';
    case 'PodFailure':
      return 'Pod Failure';
    case 'NodeIssue':
      return 'Node Issue';
    case 'Unknown':
      return 'Unknown Incident';
    default:
      return type;
  }
}

// Format a cloud incident type for display (PascalCase/abbreviations -> spaced).
function formatIncidentType(type: string): string {
  const serviceAbbreviations: Record<string, string> = {
    CLOUDTRAIL: 'CloudTrail',
    CLOUDWATCH: 'CloudWatch',
    CLOUDFRONT: 'CloudFront',
    CLOUDFORMATION: 'CloudFormation',
    DYNAMODB: 'DynamoDB',
    ELASTICACHE: 'ElastiCache',
    ELASTICSEARCH: 'Elasticsearch',
    OPENSEARCH: 'OpenSearch',
    APIGATEWAY: 'API Gateway',
    STEPFUNCTIONS: 'Step Functions',
    SECRETSMANAGER: 'Secrets Manager',
    EVENTBRIDGE: 'EventBridge',
    CODEPIPELINE: 'CodePipeline',
    CODEBUILD: 'CodeBuild',
    CODEDEPLOY: 'CodeDeploy',
    CODECOMMIT: 'CodeCommit',
    SAGEMAKER: 'SageMaker',
    GUARDDUTY: 'GuardDuty',
    SECURITYHUB: 'Security Hub',
    ACCESSANALYZER: 'Access Analyzer',
    APPRUNNER: 'App Runner',
    S3: 'S3',
    EC2: 'EC2',
    IAM: 'IAM',
    VPC: 'VPC',
    ECS: 'ECS',
    EKS: 'EKS',
    RDS: 'RDS',
    SNS: 'SNS',
    SQS: 'SQS',
    KMS: 'KMS',
    WAF: 'WAF',
    ACM: 'ACM',
    EMR: 'EMR',
    SSM: 'SSM',
    ELB: 'ELB',
    ALB: 'ALB',
    NLB: 'NLB',
    API: 'API',
    AWS: 'AWS',
  };

  const allCapsMatch = type.match(
    /^([A-Z0-9]+)(Error|Warning|Alert|Issue|Anomaly|Change|Modification|Access|Violation)?$/,
  );
  if (allCapsMatch) {
    const servicePart = allCapsMatch[1];
    const suffixPart = allCapsMatch[2] || '';
    const formattedService =
      serviceAbbreviations[servicePart] ||
      servicePart.charAt(0) + servicePart.slice(1).toLowerCase();
    if (suffixPart) {
      return `${formattedService} ${suffixPart}`;
    }
    return formattedService;
  }

  return type
    .replace(/([A-Z]{2,})([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2');
}

// Get cloud incident type label.
export function getCloudIncidentTypeLabel(type: string): string {
  switch (type) {
    case 'IAMCompromise':
      return 'IAM Compromise';
    case 'IAMPolicyViolation':
      return 'IAM Policy Violation';
    case 'IAMRoleAbuse':
      return 'IAM Role Abuse';
    case 'UnauthorizedAccess':
      return 'Unauthorized Access';
    case 'CredentialLeak':
      return 'Credential Leak';
    case 'PrivilegeEscalation':
      return 'Privilege Escalation';
    case 'S3DataExfiltration':
      return 'S3 Data Exfiltration';
    case 'S3PolicyModification':
      return 'S3 Policy Modification';
    case 'S3PublicAccess':
      return 'S3 Public Access';
    case 'S3EncryptionDisabled':
      return 'S3 Encryption Disabled';
    case 'EC2Anomaly':
      return 'EC2 Anomaly';
    case 'EC2InstanceCompromise':
      return 'EC2 Instance Compromise';
    case 'EC2UnauthorizedAccess':
      return 'EC2 Unauthorized Access';
    case 'NetworkTrafficAnomaly':
      return 'Network Traffic Anomaly';
    case 'SecurityGroupModification':
      return 'Security Group Modification';
    case 'VPCModification':
      return 'VPC Modification';
    case 'NetworkTrafficBlocked':
      return 'Network Traffic Blocked';
    case 'ServiceHealthDegradation':
      return 'Service Health Degradation';
    case 'APIThrottling':
      return 'API Throttling';
    case 'ResourceExhaustion':
      return 'Resource Exhaustion';
    case 'ConfigComplianceViolation':
      return 'Config Compliance Violation';
    case 'SecurityHubFinding':
      return 'Security Hub Finding';
    default:
      return formatIncidentType(type);
  }
}

// Render a CLI command with highlighted placeholders ({{name}}).
export function renderCommandWithPlaceholders(command: string): React.ReactNode {
  if (!command) return command;
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match;
  const regex = /\{\{([^}]+)\}\}/g;

  while ((match = regex.exec(command)) !== null) {
    if (match.index > lastIndex) {
      parts.push(command.slice(lastIndex, match.index));
    }
    const placeholderName = match[1];
    parts.push(
      <span
        key={`placeholder-${match.index}`}
        className="bg-zinc-200 dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 px-1.5 py-0.5 rounded font-medium border border-zinc-400 dark:border-zinc-600 inline-block"
        title={placeholderName}
      >
        {placeholderName}
      </span>,
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < command.length) {
    parts.push(command.slice(lastIndex));
  }

  return parts.length > 0 ? parts : command;
}

// GitHub-style line diff used by both fix cards.
export const createDiff = (
  oldText: string,
  newText: string,
): Array<{ line: string; type: 'unchanged' | 'removed' | 'added'; lineNumber?: number }> => {
  const changes = Diff.diffLines(oldText, newText);
  const result: Array<{
    line: string;
    type: 'unchanged' | 'removed' | 'added';
    lineNumber?: number;
  }> = [];

  let oldLineNumber = 1;
  let newLineNumber = 1;

  changes.forEach((change) => {
    const lines = change.value.split('\n');
    if (lines[lines.length - 1] === '') {
      lines.pop();
    }

    lines.forEach((line) => {
      if (change.removed) {
        result.push({ line, type: 'removed', lineNumber: oldLineNumber++ });
      } else if (change.added) {
        result.push({ line, type: 'added', lineNumber: newLineNumber++ });
      } else {
        result.push({ line, type: 'unchanged', lineNumber: oldLineNumber });
        oldLineNumber++;
        newLineNumber++;
      }
    });
  });

  return result;
};
